import React                   from 'react';
import { observer }            from 'mobx-react-lite';
import View                    from '@widesk-ui/components/View';
import Button                  from '@widesk-ui/components/Button';
import Skeleton                from '@widesk-ui/components/Skeleton';
import Spin                    from '@widesk-ui/components/Spin';
import useDesignTokens         from '@/hooks/useDesignTokens';
import planImportStore         from '@/TokamapEditor/planImport/planImportStore';
import { matchOptions }        from '@/TokamapEditor/planImport/planMatchOptions';
import { readyPlaceTypes }     from '@/TokamapEditor/planImport/planMatchOptions';

const OPTIONS_MAX_HEIGHT = 260;

const PlanMatchChoice = observer(() => {
	const tokens = useDesignTokens();
	const [showAll, setShowAll] = React.useState(false);
	const { match, measuring } = planImportStore;

	if (!planImportStore.readyCount) {
		return <View size={13} color={tokens.text.secondary}>Choisissez d’abord le type de POI.</View>;
	}

	if (!match) {
		if (!measuring) {
			return <View size={13} color={tokens.text.secondary}>Aucune information sur les POI déjà présents.</View>;
		}

		return (
			<View gap={8} aria-busy="true">
				<View row gap={8} style={{ alignItems: 'center' }}>
					<Spin size="small" />
					<View size={13} color={tokens.text.secondary}>Recherche des POI déjà présents sur la carte…</View>
				</View>
				<Skeleton active title={false} paragraph={{ rows: 2, width: ['100%', '100%'] }} />
			</View>
		);
	}

	const options = matchOptions(match.candidates, readyPlaceTypes());

	const existing = Math.max(0, ...options.map(option => option.existing));
	const current = planImportStore.matchChoice
		?? planImportStore.sharedMatchField
		?? options.find(option => option.recommended)?.field
		?? (options.some(option => 'label' === option.field) ? 'label' : undefined);

	if (!options.some(option => option.existing)) {
		return <View size={13} color={tokens.text.secondary}>Aucun POI de ce type sur la carte : tous seront créés.</View>;
	}

	const useful = options.filter(option => option.matched || option.field === current);
	const hidden = options.filter(option => !useful.includes(option));
	const shown = showAll || !useful.length ? options : useful;

	return (
		<View gap={8}>
			<View size={13} color={tokens.text.secondary}>
				Quand un POI du plan existe déjà sur la carte, il est mis à jour au lieu d’être recréé. Comment le reconnaître ?
			</View>

			{!options.some(option => option.matched) && (
				<View size={13}>
					Aucun des {existing} POI de la carte ne correspond aux numéros du plan : tous seront créés.
				</View>
			)}

			{measuring && (
				<View row gap={8} style={{ alignItems: 'center' }}>
					<Spin size="small" />
					<View size={12} color={tokens.text.secondary}>Mise à jour…</View>
				</View>
			)}

			<div
				role="radiogroup"
				aria-busy={measuring}
				style={{
					display: 'flex',
					flexDirection: 'column',
					gap: 6,
					maxHeight: OPTIONS_MAX_HEIGHT,
					overflowY: 'auto',
					paddingRight: 2,
					opacity: measuring ? 0.5 : 1,
					pointerEvents: measuring ? 'none' : undefined,
					transition: 'opacity 0.2s',
				}}
			>
				{shown.map(option => {
					const checked = current === option.field;

					return (
						<label
							key={option.field}
							style={{
								display: 'flex',
								gap: 8,
								alignItems: 'center',
								padding: '6px 10px',
								borderRadius: 8,
								cursor: 'pointer',
								border: `1px solid ${checked ? tokens.color.primary : tokens.border.subtle}`,
								background: checked ? `color-mix(in srgb, ${tokens.color.primary} 8%, transparent)` : 'transparent',
							}}
						>
							<input
								type="radio"
								name="plan-match-field"
								checked={checked}
								onChange={() => planImportStore.setMatchFieldForAll(option.field)}
							/>
							<View flex size={13} style={{ fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
								Par « {option.label} »
							</View>
							{option.recommended && (
								<span
									style={{
										flex: '0 0 auto',
										fontSize: 11,
										padding: '1px 6px',
										borderRadius: 999,
										color: tokens.color.success,
										border: `1px solid color-mix(in srgb, ${tokens.color.success} 40%, transparent)`,
									}}
								>
									Recommandé
								</span>
							)}
							<span
								title={`${option.matched} POI reconnus sur ${option.existing}`}
								style={{ flex: '0 0 auto', fontSize: 12, color: tokens.text.secondary }}
							>
								{option.matched} / {option.existing} reconnus
							</span>
						</label>
					);
				})}
			</div>

			{0 < hidden.length && (
				<Button size="small" type="link" style={{ padding: 0, alignSelf: 'flex-start' }} onClick={() => setShowAll(!showAll)}>
					{showAll ? 'Masquer les champs sans résultat' : `Afficher les ${hidden.length} autres champs (aucun POI reconnu)`}
				</Button>
			)}
		</View>
	);
});

export default PlanMatchChoice;
